import { useState } from "react";
import { Layout } from "@/components/layout";
import { 
  BarChart3, 
  DollarSign, 
  TrendingUp,
  Wallet,
  RefreshCw,
  Download
} from "lucide-react";
import { format, subDays } from "date-fns";
import { Button } from "@/components/ui/button";
import { useBusinessContext } from "@/providers/business-provider";
import { useQuery } from "@tanstack/react-query";
import { useToast } from "@/hooks/use-toast";

// Simple data fetching for reports
const useSimpleReportsData = (businessId: number) => {
  const today = new Date();
  const thirtyDaysAgo = subDays(today, 30);
  const startDate = format(thirtyDaysAgo, 'yyyy-MM-dd');
  const endDate = format(today, 'yyyy-MM-dd');

  // Fetch profit & loss data
  const profitLossQuery = useQuery({
    queryKey: [`/api/businesses/${businessId}/reports/profit-loss`, startDate, endDate],
    queryFn: () => fetch(`/api/businesses/${businessId}/reports/profit-loss?startDate=${startDate}&endDate=${endDate}`).then(res => res.json()),
    enabled: !!businessId,
  });

  // Fetch cash flow data
  const cashFlowQuery = useQuery({
    queryKey: [`/api/businesses/${businessId}/reports/cash-flow`, startDate, endDate],
    queryFn: () => fetch(`/api/businesses/${businessId}/reports/cash-flow?startDate=${startDate}&endDate=${endDate}`).then(res => res.json()),
    enabled: !!businessId,
  });

  // Fetch balance sheet data
  const balanceSheetQuery = useQuery({
    queryKey: [`/api/businesses/${businessId}/reports/balance-sheet`, startDate, endDate],
    queryFn: () => fetch(`/api/businesses/${businessId}/reports/balance-sheet?startDate=${startDate}&endDate=${endDate}`).then(res => res.json()),
    enabled: !!businessId,
  });

  // Fetch expense analysis
  const expenseAnalysisQuery = useQuery({
    queryKey: [`/api/businesses/${businessId}/reports/expense-analysis`, startDate, endDate],
    queryFn: () => fetch(`/api/businesses/${businessId}/reports/expense-analysis?startDate=${startDate}&endDate=${endDate}`).then(res => res.json()),
    enabled: !!businessId,
  });

  return {
    profitLoss: profitLossQuery.data,
    cashFlow: cashFlowQuery.data,
    balanceSheet: balanceSheetQuery.data,
    expenseAnalysis: expenseAnalysisQuery.data,
    isLoading: profitLossQuery.isLoading || cashFlowQuery.isLoading || balanceSheetQuery.isLoading || expenseAnalysisQuery.isLoading,
  };
};

export default function ReportsSimplePage() {
  const { activeBusiness } = useBusinessContext();
  const reportsData = useSimpleReportsData(activeBusiness?.id || 0);
  const { toast } = useToast();

  const formatCurrency = (amount: number | string) => {
    const num = typeof amount === 'string' ? parseFloat(amount) : amount;
    return new Intl.NumberFormat('en-US', {
      style: 'currency',
      currency: 'USD',
    }).format(num || 0);
  };

  const downloadReport = async (reportType: string) => {
    if (!activeBusiness) return;
    
    try {
      const today = new Date();
      const thirtyDaysAgo = subDays(today, 30);
      const startDate = format(thirtyDaysAgo, 'yyyy-MM-dd');
      const endDate = format(today, 'yyyy-MM-dd');
      
      const response = await fetch(`/api/businesses/${activeBusiness.id}/reports/${reportType}/download?startDate=${startDate}&endDate=${endDate}`, {
        method: 'GET',
        headers: {
          'Content-Type': 'application/pdf',
        },
      });
      
      if (response.ok) {
        const blob = await response.blob();
        const url = window.URL.createObjectURL(blob);
        const a = document.createElement('a');
        a.style.display = 'none';
        a.href = url;
        a.download = `${activeBusiness.name}-${reportType}-${format(today, 'yyyy-MM-dd')}.pdf`;
        document.body.appendChild(a);
        a.click();
        window.URL.revokeObjectURL(url);
        document.body.removeChild(a);
        
        toast({
          title: "Report Downloaded",
          description: `${reportType} report has been downloaded successfully.`,
        });
      } else {
        throw new Error('Failed to download report');
      }
    } catch (error) {
      toast({
        title: "Download Failed",
        description: "Unable to download the report. Please try again.",
        variant: "destructive",
      });
    }
  };

  if (!activeBusiness) {
    return (
      <Layout>
        <div className="p-6">
          <div className="text-center py-12">
            <h2 className="text-xl font-semibold mb-2">No Business Selected</h2>
            <p className="text-muted-foreground">Please select a business to view reports.</p>
          </div>
        </div>
      </Layout>
    );
  }

  return (
    <Layout>
      <div className="p-6">
        <div className="mb-6">
          <h1 className="text-3xl font-bold">Financial Reports</h1>
          <p className="text-muted-foreground">Real-time financial data for {activeBusiness.name}</p>
        </div>

        {reportsData.isLoading ? (
          <div className="flex items-center justify-center py-12">
            <RefreshCw className="h-8 w-8 animate-spin mr-2" />
            <span>Loading financial reports...</span>
          </div>
        ) : (
          <div className="grid gap-6">
            {/* Profit & Loss Summary */}
            {reportsData.profitLoss && (
              <div className="bg-white p-6 rounded-lg border">
                <div className="flex items-center justify-between mb-4">
                  <div className="flex items-center">
                    <DollarSign className="h-6 w-6 mr-2 text-green-600" />
                    <h2 className="text-xl font-semibold">Profit & Loss (Last 30 Days)</h2>
                  </div>
                  <Button
                    onClick={() => downloadReport('profit-loss')}
                    variant="outline"
                    size="sm"
                    className="flex items-center gap-2"
                  >
                    <Download className="h-4 w-4" />
                    Download PDF
                  </Button>
                </div>
                <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                  <div className="bg-green-50 p-4 rounded-lg">
                    <p className="text-sm text-green-600 font-medium">Revenue</p>
                    <p className="text-2xl font-bold text-green-800">
                      {formatCurrency(reportsData.profitLoss.revenue)}
                    </p>
                  </div>
                  <div className="bg-red-50 p-4 rounded-lg">
                    <p className="text-sm text-red-600 font-medium">Expenses</p>
                    <p className="text-2xl font-bold text-red-800">
                      {formatCurrency(reportsData.profitLoss.expenses)}
                    </p>
                  </div>
                  <div className="bg-blue-50 p-4 rounded-lg">
                    <p className="text-sm text-blue-600 font-medium">Net Income</p>
                    <p className="text-2xl font-bold text-blue-800">
                      {formatCurrency(reportsData.profitLoss.netIncome)}
                    </p>
                  </div>
                </div>
              </div>
            )}

            {/* Cash Flow Summary */}
            {reportsData.cashFlow && (
              <div className="bg-white p-6 rounded-lg border">
                <div className="flex items-center mb-4">
                  <Wallet className="h-6 w-6 mr-2 text-blue-600" />
                  <h2 className="text-xl font-semibold">Cash Flow (Last 30 Days)</h2>
                </div>
                <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                  <div className="bg-green-50 p-4 rounded-lg">
                    <p className="text-sm text-green-600 font-medium">Cash Inflows</p>
                    <p className="text-2xl font-bold text-green-800">
                      {formatCurrency(reportsData.cashFlow.inflows)}
                    </p>
                  </div>
                  <div className="bg-red-50 p-4 rounded-lg">
                    <p className="text-sm text-red-600 font-medium">Cash Outflows</p>
                    <p className="text-2xl font-bold text-red-800">
                      {formatCurrency(reportsData.cashFlow.outflows)}
                    </p>
                  </div>
                  <div className="bg-blue-50 p-4 rounded-lg">
                    <p className="text-sm text-blue-600 font-medium">Net Cash Flow</p>
                    <p className="text-2xl font-bold text-blue-800">
                      {formatCurrency(reportsData.cashFlow.netCashFlow)}
                    </p>
                  </div>
                </div>
              </div>
            )}

            {/* Balance Sheet Summary */}
            {reportsData.balanceSheet && (
              <div className="bg-white p-6 rounded-lg border">
                <div className="flex items-center justify-between mb-4">
                  <div className="flex items-center">
                    <BarChart3 className="h-6 w-6 mr-2 text-purple-600" />
                    <h2 className="text-xl font-semibold">Balance Sheet</h2>
                  </div>
                  <Button
                    onClick={() => downloadReport('balance-sheet')}
                    variant="outline"
                    size="sm"
                    className="flex items-center gap-2"
                  >
                    <Download className="h-4 w-4" />
                    Download PDF
                  </Button>
                </div>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <div className="bg-purple-50 p-4 rounded-lg">
                    <p className="text-sm text-purple-600 font-medium">Total Assets</p>
                    <p className="text-2xl font-bold text-purple-800">
                      {formatCurrency(reportsData.balanceSheet.assets?.total || 0)}
                    </p>
                  </div>
                  <div className="bg-orange-50 p-4 rounded-lg">
                    <p className="text-sm text-orange-600 font-medium">Total Liabilities</p>
                    <p className="text-2xl font-bold text-orange-800">
                      {formatCurrency(reportsData.balanceSheet.liabilities?.total || 0)}
                    </p>
                  </div>
                </div>
              </div>
            )}

            {/* Expense Analysis */}
            {reportsData.expenseAnalysis && (
              <div className="bg-white p-6 rounded-lg border">
                <div className="flex items-center mb-4">
                  <TrendingUp className="h-6 w-6 mr-2 text-red-600" />
                  <h2 className="text-xl font-semibold">Expense Analysis (Last 30 Days)</h2>
                </div>
                <div className="bg-red-50 p-4 rounded-lg">
                  <p className="text-sm text-red-600 font-medium">Total Expenses</p>
                  <p className="text-2xl font-bold text-red-800">
                    {formatCurrency(reportsData.expenseAnalysis.total)}
                  </p>
                </div>
              </div>
            )}
          </div>
        )}
      </div>
    </Layout>
  );
}